import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { CloseIcon } from '../../../../theme/overrides/CustomIcons';
import { dispatch } from '../../../../redux/store';
import VoucherProjects from '../../../../sections/voucher_modal/VoucherProjects';
import { FormProvider, RHFTextField } from '../../../../components/hook-form';
import { setIsGivingBasketOpenEvent, setIsValidEvent, setVoucherCodeEvent } from '../../../../redux/slices/voucher';
import { consumeVoucher } from '../../../../services/voucher.services';
import { removeAllProjectsEvent } from '../../../../redux/slices/donation';
import { DonationAPI } from '../../../../services/donation.services';

export default function VoucherGivingBasketModal() {
  const { isGivingBasketOpen: isOpen, voucherCode } = useSelector((state) => state.voucher);
  const { projects } = useSelector((state) => state.donation);
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [project, setProject] = useState();
  const [isAnonymous, setIsAnonymous] = useState(false);

  const resolver = yupResolver(
    Yup.object()
      .shape({
        firstName: isAnonymous ? Yup.string() : Yup.string().required('Required'),
        lastName: isAnonymous ? Yup.string() : Yup.string().required('Required'),
        email: isAnonymous ? Yup.string() : Yup.string(),
        phone: isAnonymous ? Yup.string() : Yup.string(),
      })
      .test((e) => {
        if (!isAnonymous && !e.phone && !e.email) {
          setError('Please enter either email or phone number');
        }
        return isAnonymous || !!e.email || !!e.phone;
      })
  );

  useEffect(() => {
    if (projects?.length) {
      const formatted = Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(100);
      setProject({ ...projects.at(0), amount: formatted });
    } else {
      setProject();
    }
  }, [projects]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, {
        variant: 'error',
        onExit: () => {
          setError();
        },
      });
    }
  }, [error]);

  const methods = useForm({ resolver });

  const { handleSubmit, reset } = methods;

  const onSubmit = async (data) => {
    if (!project) {
      setError('Please select a project first.');
      return;
    }

    const donorData = isAnonymous
      ? {
          firstName: 'Anonymous',
          lastName: 'Anonymous',
          email: 'anonymous@user.com',
          phone: '09111111111',
        }
      : data;

    setIsLoading(true);
    try {
      await consumeVoucher({
        ...donorData,
        donor: donorData.email ?? donorData.phone ?? 'anonymous',
        code: voucherCode,
      });
      const payload = createPayload({ formData: donorData, projects: [project] });
      await DonationAPI.submitVoucherDonation({ details: payload, voucherCode });
      enqueueSnackbar('Donation submitted!');
      onClose();
      dispatch(setVoucherCodeEvent(null));
      dispatch(setIsValidEvent(false));
      dispatch(removeAllProjectsEvent());
      window.location = '/resultpage/0/success';
    } catch (e) {
      setError(e.message);
    }
    setIsLoading(false);
  };

  const onClose = () => {
    dispatch(setIsGivingBasketOpenEvent(false));
    reset();
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={isOpen}>
      <DialogTitle>
        <Typography variant="h5" mb={2}>
          Giving Basket ({projects.length} project{projects.length > 1 ? 's' : ''} selected)
        </Typography>
        <IconButton
          aria-label="close"
          onClick={() => {
            onClose();
          }}
          sx={{
            position: 'absolute',
            right: 20,
            top: 22,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ overflowX: 'hidden' }}>
        <Stack spacing={3}>
          <Box p={1} bgcolor="orange" borderRadius={1}>
            <Grid container justifyContent="space-between">
              <Grid item>
                <Typography fontWeight={600} color="white">
                  Voucher Code In Use:
                </Typography>
              </Grid>
              <Grid item>
                <Typography fontWeight={600} color="white">
                  {voucherCode}
                </Typography>
              </Grid>
            </Grid>
          </Box>
          <VoucherProjects project={project} amount={1000} voucherCode={voucherCode} />
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              bgcolor: '#fafafaff',
              borderRadius: 1,
            }}
          >
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
              <Stack width={'100%'} alignItems="center">
                <Box width={'100%'} maxWidth={'md'}>
                  <Typography sx={{ mb: { xs: 2, sm: 3 } }}>Donor Details</Typography>

                  <FormControlLabel
                    sx={{  mb: 3 }}
                    control={
                      <Checkbox
                        checked={isAnonymous}
                        onChange={(e) => setIsAnonymous(e.target.checked)}
                      />
                    }
                    label="Make me anonymous"
                  />

                  {!isAnonymous && (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={6}>
                        <RHFTextField
                          fullWidth
                          name="firstName"
                          label="First Name"
                          placeholder="First Name"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <RHFTextField
                          fullWidth
                          name="lastName"
                          label="Last Name"
                          placeholder="Last Name"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <RHFTextField
                          fullWidth
                          type="email"
                          name="email"
                          label="Email"
                          placeholder="Email"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <RHFTextField
                          fullWidth
                          type="tel"
                          name="phone"
                          label="Phone Number"
                          placeholder="Phone Number"
                        />
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </Stack>
              <Box display="flex" justifyContent="center" mt={5}>
                <LoadingButton
                  loading={isLoading}
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{ minWidth: '150px' }}
                >
                  SUBMIT
                </LoadingButton>
              </Box>
            </FormProvider>
          </Paper>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

const createPayload = ({ formData, projects }) => {
  const payload = {
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    contactNumber: formData.phone,
    donationAmount: 100,
    projects: projects.map((e) => ({
      ...e,
      charityName: e.charityDetails.name,
      id: e._id,
      amount: parseFloat(e.amount),
    })),
  };
  return payload;
};
